import { create } from 'zustand';

export type PortalMode = 'staff' | 'student' | 'parent';

// 1) Typed config for labels (icons optional)
export const PORTAL_MODE_CONFIG = {
    staff:   { label: 'Staff Portal' },
    student: { label: 'Student Portal' },
    parent:  { label: 'Parent Portal' },
} satisfies Record<PortalMode, { label: string }>

// 2) Stable order for UI
export const PORTAL_MODES = ['staff', 'student', 'parent'] as const satisfies readonly PortalMode[]

// 3) Ready-to-map list (includes key + label)
export const PORTAL_MODE_OPTIONS = PORTAL_MODES.map((key) => ({ key, ...PORTAL_MODE_CONFIG[key] }))

type PortalState = {
    hostMode: PortalMode;                  // decided at boot from subdomain
    viewMode: PortalMode;                  // persona ("view as")
    setViewMode: (m: PortalMode) => void;
    allowedViewModes: PortalMode[];        // set after auth/RBAC lookup
    setAllowedViewModes: (modes: PortalMode[]) => void;
};

function detectHostMode(): PortalMode {

    if (import.meta.env.DEV) {
        return import.meta.env.VITE_DEV_HOST_MODE as PortalMode || 'staff';
    }

    const host = window.location.hostname;
    if (host.startsWith('staff')) return 'staff';
    if (host.startsWith('student')) return 'student';
    return 'parent';
}

const LS_KEY = 'portal.viewMode';

export const usePortalModeStore = create<PortalState>((set, get) => {
    const hostMode = detectHostMode();

    // clamp saved to host-legal default (until RBAC arrives)
    const initial = hostMode;

    return {
        hostMode,
        viewMode: initial,
        setViewMode: (m) => {
            const { allowedViewModes } = get();
            if (allowedViewModes.length && !allowedViewModes.includes(m)) return;
            localStorage.setItem(LS_KEY, m);
            set({ viewMode: m });
        },
        allowedViewModes: hostMode === 'staff' ? ['staff', 'student', 'parent'] : [hostMode],
        setAllowedViewModes: (modes) => set({ allowedViewModes: modes }),
    };
});

// Handy selectors (avoid re-renders)
export const useHostMode  = () => usePortalModeStore(s => s.hostMode);
export const useViewMode  = () => usePortalModeStore(s => s.viewMode);
export const useSetViewMode = () => usePortalModeStore(s => s.setViewMode);
export const useAllowedViewModes = () => usePortalModeStore(s => s.allowedViewModes);
